/**
 * CSKRadar — Pentagon radar chart for the 5 CSK factors (S, H, D, R, E).
 * Pure SVG implementation — no chart library dependency.
 */
import React from 'react';
import type { CSKFactors } from '@/lib/smartblocksApi';

type CSKRadarProps = {
  factors: CSKFactors;
  size?: number;
};

const FACTOR_LABELS: { key: keyof CSKFactors; label: string; fullLabel: string }[] = [
  { key: 'S', label: 'S', fullLabel: 'Structural' },
  { key: 'H', label: 'H', fullLabel: 'Handling' },
  { key: 'D', label: 'D', fullLabel: 'Balance' },
  { key: 'R', label: 'R', fullLabel: 'Resource' },
  { key: 'E', label: 'E', fullLabel: 'Conversion' },
];

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (Math.PI / 180) * angleDeg;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

export function CSKRadar({ factors, size = 200 }: CSKRadarProps) {
  const cx = size / 2;
  const cy = size / 2;
  const maxR = size / 2 - 30;
  const n = FACTOR_LABELS.length;
  const angleStep = 360 / n;
  const startAngle = -90; // top

  // Grid rings at 25%, 50%, 75%, 100%
  const rings = [0.25, 0.5, 0.75, 1.0];

  // Data polygon points
  const dataPoints = FACTOR_LABELS.map((f, i) => {
    const angle = startAngle + i * angleStep;
    const value = factors[f.key] ?? 0;
    return polarToCartesian(cx, cy, maxR * value, angle);
  });
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Grid rings */}
        {rings.map((r) => {
          const ringPoints = Array.from({ length: n }, (_, i) => {
            const angle = startAngle + i * angleStep;
            return polarToCartesian(cx, cy, maxR * r, angle);
          });
          const ringPath = ringPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
          return (
            <path
              key={r}
              d={ringPath}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth={1}
            />
          );
        })}

        {/* Axis lines */}
        {FACTOR_LABELS.map((_, i) => {
          const angle = startAngle + i * angleStep;
          const end = polarToCartesian(cx, cy, maxR, angle);
          return (
            <line
              key={i}
              x1={cx}
              y1={cy}
              x2={end.x}
              y2={end.y}
              stroke="#e5e7eb"
              strokeWidth={1}
            />
          );
        })}

        {/* Data polygon */}
        <path
          d={dataPath}
          fill="rgba(16, 185, 129, 0.2)"
          stroke="#059669"
          strokeWidth={2}
          className="transition-all duration-500 ease-out"
        />

        {/* Data points */}
        {dataPoints.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={3}
            fill="#059669"
            className="transition-all duration-500 ease-out"
          />
        ))}

        {/* Labels */}
        {FACTOR_LABELS.map((f, i) => {
          const angle = startAngle + i * angleStep;
          const labelPos = polarToCartesian(cx, cy, maxR + 18, angle);
          return (
            <text
              key={f.key}
              x={labelPos.x}
              y={labelPos.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-cocoa text-xs font-semibold"
              fontSize={11}
            >
              {f.label}
            </text>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="mt-1 flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs text-cocoa/60">
        {FACTOR_LABELS.map((f) => (
          <span key={f.key}>
            <span className="font-semibold text-cocoa">{f.label}</span> {f.fullLabel}{' '}
            <span className="text-emerald-600">{Math.round((factors[f.key] ?? 0) * 100)}%</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default CSKRadar;
